import React, { useEffect, useRef, useState } from 'react';
import styles from './styles/Pagination.module.css';

const range = (start, end) =>
    Array.from({ length: Math.max(end - start + 1, 0) }, (_, i) => start + i);

/**
 * Build a truncated page list: always-visible boundary pages at each end, a
 * sliding window of siblings around the current page, and ellipses for the gaps.
 * e.g. 1 … 17 18 [19] 20 21 … 39
 */
const buildPageItems = (page, count, siblingCount, boundaryCount) => {
    const startPages = range(1, Math.min(boundaryCount, count));
    const endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);

    const siblingsStart = Math.max(
        Math.min(page - siblingCount, count - boundaryCount - siblingCount * 2 - 1),
        boundaryCount + 2
    );
    const siblingsEnd = Math.min(
        Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
        endPages.length > 0 ? endPages[0] - 2 : count - 1
    );

    return [
        ...startPages,
        // Render a real page number instead of an ellipsis when the gap is a single page.
        ...(siblingsStart > boundaryCount + 2
            ? ['start-ellipsis']
            : boundaryCount + 1 < count - boundaryCount
                ? [boundaryCount + 1]
                : []),
        ...range(siblingsStart, siblingsEnd),
        ...(siblingsEnd < count - boundaryCount - 1
            ? ['end-ellipsis']
            : count - boundaryCount > boundaryCount
                ? [count - boundaryCount]
                : []),
        ...endPages,
    ];
};

/**
 * Forum-style pagination for any list that pages through a known total.
 *
 * Renders a bounded number of controls no matter how many pages exist, so the
 * bar never overflows its container: first/last pages are always shown, a
 * window of siblings surrounds the current page, and collapsed ranges become
 * clickable "…" buttons that expand into a jump-to-page input.
 *
 * Fully controlled — the parent owns `currentPage` and refetches in `onPageChange`.
 * Renders nothing when there is a single page or fewer.
 *
 * @param {number} currentPage - 1-indexed active page.
 * @param {number} totalPages - Total number of pages available.
 * @param {(page: number) => void} onPageChange - Called with the requested page.
 * @param {number} [siblingCount=1] - Pages shown either side of the current page.
 * @param {number} [boundaryCount=1] - Pages always shown at the start and end.
 * @param {boolean} [showPrevNext=true] - Show the Prev/Next buttons.
 * @param {string} [className] - Extra class for the wrapping nav element.
 * @param {string} [ariaLabel='Pagination'] - Accessible name for the nav landmark.
 */
const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    siblingCount = 1,
    boundaryCount = 1,
    showPrevNext = true,
    className = '',
    ariaLabel = 'Pagination',
}) => {
    // Which ellipsis (if any) has been expanded into a jump-to-page input.
    const [jumpTarget, setJumpTarget] = useState(null);
    const [jumpValue, setJumpValue] = useState('');
    const jumpInputRef = useRef(null);

    useEffect(() => {
        if (jumpTarget) jumpInputRef.current?.focus();
    }, [jumpTarget]);

    // Collapse the jump input whenever the page changes out from under us.
    useEffect(() => {
        setJumpTarget(null);
        setJumpValue('');
    }, [currentPage, totalPages]);

    if (!totalPages || totalPages <= 1) return null;

    const page = Math.min(Math.max(currentPage || 1, 1), totalPages);

    const goTo = (target) => {
        const next = Math.min(Math.max(target, 1), totalPages);
        if (next !== page) onPageChange(next);
    };

    const openJump = (which) => {
        setJumpTarget(which);
        setJumpValue('');
    };

    const closeJump = () => {
        setJumpTarget(null);
        setJumpValue('');
    };

    const commitJump = () => {
        const parsed = parseInt(jumpValue, 10);
        if (!Number.isNaN(parsed)) goTo(parsed);
        closeJump();
    };

    const handleJumpKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitJump();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            closeJump();
        }
    };

    const renderEllipsis = (which) => {
        if (jumpTarget === which) {
            return (
                <input
                    key={which}
                    ref={jumpInputRef}
                    type="number"
                    min={1}
                    max={totalPages}
                    className={styles.jumpInput}
                    value={jumpValue}
                    placeholder=""
                    aria-label={`Jump to page (1 to ${totalPages})`}
                    onChange={(e) => setJumpValue(e.target.value)}
                    onKeyDown={handleJumpKeyDown}
                    onBlur={commitJump}
                />
            );
        }
        return (
            <button
                key={which}
                type="button"
                className={styles.ellipsis}
                onClick={() => openJump(which)}
                title="Jump to page"
                aria-label="Jump to a specific page"
            >
                …
            </button>
        );
    };

    const items = buildPageItems(page, totalPages, siblingCount, boundaryCount);

    return (
        <nav className={`${styles.pagination} ${className}`} aria-label={ariaLabel}>
            {showPrevNext && (
                <button
                    type="button"
                    className={`${styles.pageBtn} ${styles.navBtn}`}
                    onClick={() => goTo(page - 1)}
                    disabled={page === 1}
                    aria-label="Previous page"
                >
                    ‹ Prev
                </button>
            )}

            {items.map((item) =>
                typeof item === 'number' ? (
                    <button
                        type="button"
                        key={item}
                        className={`${styles.pageBtn} ${item === page ? styles.activePage : ''}`}
                        onClick={() => goTo(item)}
                        aria-label={`Page ${item}`}
                        aria-current={item === page ? 'page' : undefined}
                    >
                        {item}
                    </button>
                ) : (
                    renderEllipsis(item)
                )
            )}

            {showPrevNext && (
                <button
                    type="button"
                    className={`${styles.pageBtn} ${styles.navBtn}`}
                    onClick={() => goTo(page + 1)}
                    disabled={page === totalPages}
                    aria-label="Next page"
                >
                    Next ›
                </button>
            )}
        </nav>
    );
};

export default Pagination;
